/**
 * Weapons. Authored along +Y with the grip at the origin (y = 0), blade up,
 * width on Z, thickness on X — so a weapon can be dropped onto a hand bone
 * with a single socket transform.
 *
 * Blades are lofts with a low superellipse exponent, which gives a real
 * lens/diamond cross-section that tapers to an edge instead of a flat slab —
 * that is what makes the key light streak along the steel.
 *
 * Every weapon uses the shared character atlas material, so a warrior + weapon
 * is still only two draw calls, and the crowd renderer can instance the raw
 * geometry from `WEAPON_GEO`.
 */
import * as THREE from 'three';
import { loft, sheet, place, mergeAll, tileUV, paint, indexed, triCount } from './geom.js';

const PI = Math.PI;
const _c = new THREE.Color();

/** Collect parts with per-part tile + tint, then merge into one geometry. */
class WeaponBuilder {
  constructor(uv) { this.uv = uv; this.parts = []; }
  add(g, tile, tint, uvOpts) {
    g = indexed(g);
    const rect = this.uv[tile];
    if (rect) tileUV(g, rect, uvOpts || {});
    _c.setRGB(tint[0], tint[1], tint[2]);
    paint(g, _c);
    this.parts.push(g);
    return this;
  }
  build() { const g = mergeAll(this.parts); this.parts.length = 0; g.computeBoundingSphere(); return g; }
}

const W = [1, 1, 1];
const DARK = [0.72, 0.72, 0.75];
const WARM = [1.05, 0.98, 0.85];

/** straight or curved blade: rings of {y, thick, width, curve} */
function blade(rings, seg = 6) {
  return loft(rings.map(r => ({ y: r[0], rx: r[1], rz: r[2], cz: r[3] || 0, sq: r[4] ?? 1.25 })),
    { seg, capBottom: true, capTop: false, vFromY: true, phase: 0 });
}

function shaft(y0, y1, r0, r1, seg = 7) {
  return loft([
    { y: y0, rx: r0, rz: r0, sq: 2 },
    { y: (y0 + y1) * 0.5, rx: (r0 + r1) * 0.5, rz: (r0 + r1) * 0.5, sq: 2 },
    { y: y1, rx: r1, rz: r1, sq: 2 },
  ], { seg, capTop: true, capBottom: true, vFromY: true, uRepeat: 1 });
}

function ring(y, r, h, seg = 7) {
  return loft([
    { y: y - h / 2, rx: r, rz: r, sq: 2.2 },
    { y: y + h / 2, rx: r, rz: r, sq: 2.2 },
  ], { seg, capTop: true, capBottom: true, vFromY: true });
}

function tassel(y, len, w, phase = 0) {
  return sheet((u, v, o) => {
    const yy = y - len * v;
    const sway = Math.sin(v * 2.4 + phase) * len * 0.18 * v;
    o.set((u - 0.5) * w * (1 + v * 0.6), yy, sway);
  }, 2, 3, { thickness: 0.004 });
}

/* ------------------------------------------------------------------ models */

const MODELS = {
  /** Hero weapon: a huge crescent-bladed glaive (guan dao). */
  greatGlaive(B, k = 1) {
    B.add(shaft(-0.70 * k, 1.02 * k, 0.024 * k, 0.021 * k, 8), 'wood', WARM, { sv: 3 });
    B.add(ring(1.00 * k, 0.033 * k, 0.070 * k), 'gold', W);
    B.add(ring(-0.68 * k, 0.031 * k, 0.065 * k), 'gold', W);
    B.add(ring(0.22 * k, 0.029 * k, 0.050 * k), 'gold', W);
    // grip wrap
    B.add(shaft(-0.18 * k, 0.18 * k, 0.028 * k, 0.028 * k, 8), 'leather', DARK, { sv: 4 });
    // main crescent blade
    B.add(blade([
      [1.04 * k, 0.016 * k, 0.038 * k, 0.00],
      [1.18 * k, 0.014 * k, 0.088 * k, 0.032 * k],
      [1.38 * k, 0.012 * k, 0.115 * k, 0.080 * k],
      [1.58 * k, 0.010 * k, 0.100 * k, 0.128 * k],
      [1.76 * k, 0.007 * k, 0.060 * k, 0.148 * k],
      [1.88 * k, 0.004 * k, 0.015 * k, 0.136 * k],
    ], 6), 'steel', W, { sv: 2 });
    // A thicker blackened spine sits proud of the steel faces. It leaves a
    // narrow bright cutting perimeter at gameplay distance instead of letting
    // the entire crescent flip between one white slab and one black slab.
    B.add(blade([
      [1.045 * k, 0.021 * k, 0.030 * k, 0.000],
      [1.18 * k, 0.019 * k, 0.042 * k, 0.018 * k],
      [1.38 * k, 0.017 * k, 0.052 * k, 0.048 * k],
      [1.58 * k, 0.014 * k, 0.044 * k, 0.086 * k],
      [1.76 * k, 0.009 * k, 0.024 * k, 0.122 * k],
      [1.82 * k, 0.005 * k, 0.008 * k, 0.132 * k],
    ], 6), 'iron', DARK, { sv: 1.6 });
    // Dark back-hook: a separate value mass creates the inner notch/read.
    B.add(blade([
      [1.10 * k, 0.010 * k, 0.030 * k, -0.012 * k],
      [1.24 * k, 0.009 * k, 0.050 * k, -0.074 * k],
      [1.34 * k, 0.006 * k, 0.026 * k, -0.118 * k],
      [1.38 * k, 0.003 * k, 0.008 * k, -0.132 * k],
    ], 5), 'iron', DARK);
    // gilded blade root
    B.add(blade([
      [1.00 * k, 0.022 * k, 0.036 * k, 0],
      [1.12 * k, 0.018 * k, 0.050 * k, 0.013 * k],
      [1.19 * k, 0.014 * k, 0.036 * k, 0.021 * k],
    ], 6), 'gold', W);
    B.add(tassel(0.98 * k, 0.28 * k, 0.05 * k), 'silk', W);
    // butt spike
    B.add(blade([
      [-0.70 * k, 0.019 * k, 0.019 * k, 0],
      [-0.82 * k, 0.008 * k, 0.008 * k, 0],
    ], 6), 'iron', W);
  },

  spear(B, k = 1) {
    B.add(shaft(-0.85 * k, 1.05 * k, 0.019 * k, 0.016 * k, 7), 'wood', WARM, { sv: 4 });
    B.add(ring(1.03 * k, 0.024 * k, 0.055 * k), 'iron', W);
    B.add(blade([
      [1.06 * k, 0.013 * k, 0.020 * k, 0],
      [1.14 * k, 0.011 * k, 0.040 * k, 0],
      [1.30 * k, 0.008 * k, 0.036 * k, 0],
      [1.44 * k, 0.004 * k, 0.012 * k, 0],
    ], 6), 'steel', W);
    B.add(tassel(1.00 * k, 0.16 * k, 0.036 * k, 1.1), 'silk', W);
    B.add(blade([[-0.85 * k, 0.016 * k, 0.016 * k, 0], [-0.95 * k, 0.006 * k, 0.006 * k, 0]], 6), 'iron', W);
  },

  /** Single-edged curved sabre. */
  dao(B, k = 1) {
    B.add(shaft(-0.02 * k, 0.20 * k, 0.019 * k, 0.017 * k, 7), 'leather', DARK, { sv: 3 });
    // disc guard
    B.add(loft([
      { y: 0.20 * k, rx: 0.052 * k, rz: 0.052 * k, sq: 2 },
      { y: 0.235 * k, rx: 0.046 * k, rz: 0.046 * k, sq: 2 },
    ], { seg: 8, capTop: true, capBottom: true, vFromY: true }), 'gold', W);
    B.add(blade([
      [0.24 * k, 0.012 * k, 0.026 * k, 0.000],
      [0.42 * k, 0.011 * k, 0.031 * k, 0.012 * k],
      [0.62 * k, 0.010 * k, 0.033 * k, 0.040 * k],
      [0.80 * k, 0.009 * k, 0.034 * k, 0.082 * k],
      [0.94 * k, 0.007 * k, 0.030 * k, 0.126 * k],
      [1.02 * k, 0.003 * k, 0.010 * k, 0.152 * k],
    ], 6), 'steel', W, { sv: 2 });
    // pommel
    B.add(loft([
      { y: -0.05 * k, rx: 0.014 * k, rz: 0.014 * k, sq: 2 },
      { y: -0.02 * k, rx: 0.026 * k, rz: 0.026 * k, sq: 2 },
      { y: 0.00, rx: 0.020 * k, rz: 0.020 * k, sq: 2 },
    ], { seg: 7, capTop: true, capBottom: true, vFromY: true }), 'gold', W);
    B.add(tassel(-0.05 * k, 0.18 * k, 0.030 * k, 0.4), 'silk', W);
  },

  halberd(B, k = 1) {
    B.add(shaft(-0.80 * k, 1.10 * k, 0.021 * k, 0.018 * k, 7), 'wood', WARM, { sv: 4 });
    B.add(ring(1.02 * k, 0.026 * k, 0.05 * k), 'iron', W);
    // spike
    B.add(blade([
      [1.10 * k, 0.011 * k, 0.018 * k, 0],
      [1.26 * k, 0.009 * k, 0.026 * k, 0],
      [1.42 * k, 0.004 * k, 0.010 * k, 0],
    ], 6), 'steel', W);
    // crescent axe blade on one side
    const ax = loft([
      { y: 0.90 * k, rx: 0.007 * k, rz: 0.030 * k, cz: 0.055 * k, sq: 1.3 },
      { y: 1.00 * k, rx: 0.008 * k, rz: 0.070 * k, cz: 0.110 * k, sq: 1.3 },
      { y: 1.12 * k, rx: 0.007 * k, rz: 0.062 * k, cz: 0.140 * k, sq: 1.3 },
      { y: 1.22 * k, rx: 0.004 * k, rz: 0.024 * k, cz: 0.120 * k, sq: 1.3 },
    ], { seg: 6, capBottom: true, vFromY: true, phase: 0 });
    B.add(ax, 'steel', W);
    // rear hook
    B.add(blade([
      [1.00 * k, 0.008 * k, 0.022 * k, -0.030 * k],
      [1.10 * k, 0.007 * k, 0.030 * k, -0.080 * k],
      [1.16 * k, 0.003 * k, 0.010 * k, -0.110 * k],
    ], 5), 'iron', W);
    B.add(tassel(1.00 * k, 0.20 * k, 0.04 * k, 2.0), 'silk', W);
  },

  /** One axe of a pair — attach twice. */
  axe(B, k = 1) {
    B.add(shaft(-0.16 * k, 0.42 * k, 0.017 * k, 0.015 * k, 7), 'wood', WARM, { sv: 3 });
    B.add(shaft(-0.14 * k, 0.06 * k, 0.021 * k, 0.021 * k, 7), 'leather', DARK, { sv: 3 });
    const head = loft([
      { y: 0.30 * k, rx: 0.020 * k, rz: 0.026 * k, cz: 0.020 * k, sq: 2.0 },
      { y: 0.36 * k, rx: 0.016 * k, rz: 0.075 * k, cz: 0.062 * k, sq: 1.5 },
      { y: 0.46 * k, rx: 0.012 * k, rz: 0.085 * k, cz: 0.080 * k, sq: 1.4 },
      { y: 0.54 * k, rx: 0.008 * k, rz: 0.040 * k, cz: 0.055 * k, sq: 1.4 },
    ], { seg: 6, capBottom: true, capTop: true, vFromY: true, phase: 0 });
    B.add(head, 'steel', W);
    B.add(ring(0.30 * k, 0.024 * k, 0.03 * k), 'gold', W);
    B.add(blade([[0.42 * k, 0.014 * k, 0.014 * k, 0], [0.52 * k, 0.005 * k, 0.005 * k, 0]], 6), 'iron', W);
  },

  /**
   * GADA — the heavy blunt weapon (DEV_PLAN Step 8, weapon 1 of 3).
   *
   * A Javanese mace, and the obvious one for this setting: the gada is Bima's
   * weapon in the wayang, so a Majapahit champion carrying one needs no
   * explanation. Two-handed, which is what separates it from the axe already
   * in this file.
   *
   * THE SHAPE IS THE ARCHETYPE. Everything else in the library is an edge on a
   * long lever — the mass sits near the hand and the damage happens at a point.
   * This is the opposite: almost all of its mass is a single lump at the far
   * end, and the haft exists only to swing it. That is why the head is built
   * as one heavy ovoid with six flanges rather than as a blade, and why the
   * reach measured for it (1.05 m, the head's CENTRE, not the finial) is the
   * shortest of any two-handed weapon here. A mace that reached like a glaive
   * would just be a glaive that does not cut.
   */
  gada(B, k = 1) {
    /* THE PROPORTIONS WERE WRONG ONCE AND THE FRAME IS WHAT SAID SO.
     * The first build ran the haft from -0.84 to 0.88 with a head of 0.098
     * radius and a finial out to 1.40 — 2.24 m overall, longer than the
     * glaive, with a head 8% of its length. It photographed as a stick with a
     * ball on it, which is the exact failure the flanges were supposed to
     * prevent. Now: 1.74 m overall, the butt cut to -0.52, the head widened to
     * 0.125 and brought down to 0.88, and the whole thing darkened — a gada is
     * a lump of iron, not a polished blade, and W tint on 'iron' made the head
     * the brightest object on the hero. */
    B.add(shaft(-0.52 * k, 0.78 * k, 0.021 * k, 0.030 * k, 7), 'wood', WARM, { sv: 3 });
    B.add(shaft(-0.30 * k, 0.16 * k, 0.027 * k, 0.027 * k, 7), 'leather', DARK, { sv: 3 });
    B.add(ring(-0.52 * k, 0.034 * k, 0.048 * k), 'iron', DARK);
    B.add(ring(0.76 * k, 0.046 * k, 0.052 * k), 'gold', W);

    /* The head: widest at 0.88, which is what REACH.gada points at. A third of
     * the weapon's length and nearly a quarter of its silhouette width, so it
     * reads as the mass it is meant to be. */
    const head = loft([
      { y: 0.78 * k, rx: 0.052 * k, rz: 0.052 * k, sq: 2.0 },
      { y: 0.83 * k, rx: 0.108 * k, rz: 0.108 * k, sq: 2.2 },
      { y: 0.88 * k, rx: 0.125 * k, rz: 0.125 * k, sq: 2.3 },
      { y: 0.96 * k, rx: 0.106 * k, rz: 0.106 * k, sq: 2.2 },
      { y: 1.02 * k, rx: 0.050 * k, rz: 0.050 * k, sq: 2.0 },
    ], { seg: 6, capBottom: true, capTop: true, vFromY: true, phase: 0 });
    B.add(head, 'iron', DARK);

    /* Six flanges. A club with no flanges reads as a stick with a ball on it;
     * these are what say "mace" at the distance the camera actually sits. Thin
     * on X, proud on Z, and swept round Y by place(). Left at full brightness
     * against the darkened head, so the ridges catch the key and the lump
     * behind them does not. */
    for (let i = 0; i < 6; i++) {
      const f = blade([
        [0.79 * k, 0.012 * k, 0.064 * k, 0],
        [0.87 * k, 0.014 * k, 0.170 * k, 0],
        [0.95 * k, 0.012 * k, 0.148 * k, 0],
        [1.01 * k, 0.006 * k, 0.058 * k, 0],
      ], 4);
      B.add(place(f, { rot: [0, (i / 6) * PI * 2, 0] }), 'steel', W);
    }

    // finial: a short spike, so an overhead drop has a point at the bottom of it
    B.add(blade([
      [1.02 * k, 0.019 * k, 0.019 * k, 0],
      [1.12 * k, 0.013 * k, 0.013 * k, 0],
      [1.22 * k, 0.004 * k, 0.004 * k, 0],
    ], 5), 'steel', W);
    B.add(ring(1.02 * k, 0.030 * k, 0.030 * k), 'gold', W);
  },

  /**
   * CAMBUK NAGA — the reach weapon that is not the spear.
   * DEV_PLAN Step 8, weapon 2 of 3.
   *
   * A chain whip with a naga-headed weight: the naga is everywhere in Javanese
   * temple carving and wayang, so a serpent on the end of a chain needs no
   * more explanation than the gada did.
   *
   * WHY A CHAIN. Measured across all four existing weapons, every one of them
   * brings its business end within 0.09 m of the wielder's own axis at some
   * point in its chain — a blade is a lever and a lever pivots at the hand.
   * Nothing in the game keeps its damage OUT THERE. That is the hole this
   * fills: the head orbits at the end of 1.6 m of chain and never comes home,
   * which is a real weakness as well as a reach — the moveset gives it an
   * inner dead zone where every other weapon is at its most dangerous.
   *
   * MODELLED SEMI-RIGID, and the honesty about that is in the geometry: a real
   * chain would need a simulated rope, and this is one merged mesh like every
   * other weapon here. The links are drawn with a slight sag so the silhouette
   * reads as a flail at rest rather than a straight pole with a lump on it.
   */
  cambuk(B, k = 1) {
    // the haft: short, because the weapon's length is chain, not shaft
    B.add(shaft(-0.34 * k, 0.30 * k, 0.020 * k, 0.024 * k, 7), 'wood', WARM, { sv: 2 });
    B.add(shaft(-0.30 * k, 0.10 * k, 0.026 * k, 0.026 * k, 7), 'leather', DARK, { sv: 2 });
    B.add(ring(-0.34 * k, 0.030 * k, 0.040 * k), 'iron', DARK);
    B.add(ring(0.30 * k, 0.034 * k, 0.044 * k), 'gold', W);

    /* Eleven links from 0.36 to 1.60. The first version wrote "a dead-straight
     * chain reads as a rod" in this comment and then built one: links 0.060 m
     * long on 0.124 m centres, so 52% of the run was empty air, bowed by
     * 0.035 m over 1.24 m — 2.8%, which is nothing. Photographed, it was a row
     * of separated beads held rigidly at 45 degrees: a fishing rod at rest, and
     * in motion a DOTTED LINE, because eleven small dots with gaps between them
     * dissolve at three metres. Two numbers fix it and neither costs a triangle.
     *
     * OVERLAP. Each link is 0.136 m long on 0.124 m centres, so consecutive
     * links intersect by 0.012 m. The run is continuous, and the alternating
     * quarter-turn now reads as interlocking links rather than as beads.
     *
     * CURVE. A belly of 0.15 m (12% of the run) in Z, plus a 0.045 m helical
     * wander in X, so the line is a curve in three dimensions rather than a
     * flat bow. It is static geometry serving both a hanging chain and a chain
     * under tension, so the belly is deliberately modest: enough that it can
     * never read as a rod, not so much that full extension looks broken. */
    for (let i = 0; i < 11; i++) {
      const t = i / 10;
      const y = (0.36 + t * 1.24) * k;
      const r = (0.032 - t * 0.007) * k;
      const belly = Math.sin(t * Math.PI) * 0.150 * k;
      const wander = Math.sin(t * Math.PI * 2.2) * 0.045 * k;
      const link = loft([
        { y: -0.068 * k, rx: r * 0.50, rz: r * 0.92, sq: 2.6 },
        { y: -0.030 * k, rx: r, rz: r * 1.18, sq: 2.6 },
        { y: 0.030 * k, rx: r, rz: r * 1.18, sq: 2.6 },
        { y: 0.068 * k, rx: r * 0.50, rz: r * 0.92, sq: 2.6 },
      ], { seg: 5, capTop: true, capBottom: true, vFromY: true });
      B.add(place(link, { rot: [0, (i % 2) * (PI / 2), 0], pos: [wander, y, belly] }),
        'iron', i % 2 ? DARK : W);
    }

    /* The naga head: a tapering wedge with a flared hood and two fangs. It is
     * the only part with any mass, and at musou range it is the whole weapon. */
    const head = loft([
      { y: 1.62 * k, rx: 0.040 * k, rz: 0.040 * k, sq: 2.2 },
      { y: 1.70 * k, rx: 0.082 * k, rz: 0.070 * k, sq: 2.4 },   // hood
      { y: 1.80 * k, rx: 0.070 * k, rz: 0.062 * k, sq: 2.2 },
      { y: 1.92 * k, rx: 0.046 * k, rz: 0.050 * k, sq: 2.0 },   // snout
      { y: 2.02 * k, rx: 0.028 * k, rz: 0.034 * k, sq: 2.0 },
    ], { seg: 6, capBottom: true, capTop: true, vFromY: true });
    /* DARK, not W. At full white the head photographed as a flat pale paddle
     * with no shading and no readable silhouette — the gada's "stick with a
     * ball on it" fault in a different colour. The hood plates and fangs carry
     * the shape; the head body should sit under them, not glow through them. */
    B.add(head, 'steel', DARK);
    /* Hood flare: two thin plates either side, which is what says "naga".
     * BUILT AROUND THE ORIGIN AND MOVED UP, not built at the hood and rotated
     * there — place() rotates about the world origin and translates after, so
     * a plate authored at y 1.75 and tilted 0.42 rad swings 1.75*sin(0.42) =
     * 0.71 m sideways. That is how these shipped: a bounding box of +/-0.70 m
     * on a weapon 0.08 m wide, two gold plates hanging in mid-air either side
     * of the chain. Rotate the part about its own centre; then place it. */
    for (const sx of [-1, 1]) {
      const fin = blade([
        [-0.09 * k, 0.008 * k, 0.030 * k, 0],
        [-0.01 * k, 0.009 * k, 0.076 * k, 0],
        [0.09 * k, 0.006 * k, 0.048 * k, 0],
      ], 4);
      B.add(place(place(fin, { rot: [0, 0, sx * 0.42] }),
        { pos: [sx * 0.052 * k, 1.75 * k, 0] }), 'gold', W);
    }
    /* Fangs: a PAIR, splayed either side of the snout. The first version
     * ignored its own `sx` and built both at the same place on the centreline
     * — one visible fang with a second hidden inside it, paying for triangles
     * nobody could see. */
    for (const sx of [-1, 1]) {
      const fang = blade([
        [-0.06 * k, 0.010 * k, 0.010 * k, 0],
        [0.06 * k, 0.004 * k, 0.004 * k, 0],
      ], 4);
      B.add(place(place(fang, { rot: [0, 0, sx * 0.30] }),
        { pos: [sx * 0.018 * k, 2.00 * k, 0.010 * k] }), 'steel', W);
    }
    B.add(ring(1.66 * k, 0.048 * k, 0.024 * k), 'gold', W);
  },

  /**
   * KAPAK GARUDA — a short, heavy war axe, carried as a pair.
   *
   * Built SHORT on purpose: the weapon's whole claim is that you must be
   * inside everyone else's reach to use it, so the haft is 0.62 m where the
   * glaive's is 2.70. The mass all sits at the head, which is where a critic
   * looks first and where the gada's first version failed — a head that is a
   * small lump on a stick reads as a tool, not a weapon.
   *
   * The silhouette is a garuda's stoop: a deep crescent bit that sweeps
   * FORWARD of the haft (so the axe leads the hand through a swing), a beaked
   * back-spike opposite it, and a flared collar where the head meets the wood.
   * Crescent forward + beak back is what stops it reading as a woodsman's
   * hatchet from any angle.
   */
  kapak(B, k = 1) {
    // haft: short, thick, wrapped. A one-hand grip with no room to choke up.
    B.add(shaft(-0.20 * k, 0.42 * k, 0.021 * k, 0.018 * k, 7), 'wood', WARM, { sv: 3 });
    B.add(shaft(-0.18 * k, 0.10 * k, 0.026 * k, 0.026 * k, 7), 'leather', DARK, { sv: 3 });
    B.add(ring(-0.20 * k, 0.030 * k, 0.034 * k), 'iron', DARK);

    // collar: the flare where head meets haft, and the thing that gives the
    // join a readable edge instead of a seam
    B.add(loft([
      { y: 0.26 * k, rx: 0.030 * k, rz: 0.034 * k, sq: 2.2 },
      { y: 0.31 * k, rx: 0.042 * k, rz: 0.046 * k, sq: 2.0 },
      { y: 0.37 * k, rx: 0.031 * k, rz: 0.035 * k, sq: 2.2 },
    ], { seg: 7, capTop: true, capBottom: true, vFromY: true }), 'gold', W);

    /* The bit. Nine rings so the crescent actually curves rather than bevels:
     * rz is the width across the face, cz pushes the whole profile FORWARD of
     * the haft, and both peak in the middle of the sweep. Deliberately dark
     * steel — the cambuk's head shipped at full white and photographed as a
     * flat pale paddle. */
    const bit = [];
    for (let i = 0; i < 9; i++) {
      const t = i / 8;
      const bell = Math.sin(t * Math.PI);
      bit.push({
        y: (0.22 + t * 0.34) * k,
        rx: (0.009 + 0.007 * bell) * k,          // thickness through the blade
        rz: (0.022 + 0.098 * bell) * k,          // width across the face
        cz: (0.030 + 0.088 * bell) * k,          // thrown forward of the haft
        sq: 1.35,
      });
    }
    B.add(loft(bit, { seg: 6, capBottom: true, capTop: true, vFromY: true }), 'steel', DARK);

    // the cutting edge, a brighter sliver riding the outer rim of the bit
    const edge = [];
    for (let i = 0; i < 7; i++) {
      const t = i / 6, bell = Math.sin(t * Math.PI);
      edge.push({
        y: (0.25 + t * 0.28) * k,
        rx: 0.004 * k,
        rz: (0.010 + 0.020 * bell) * k,
        cz: (0.048 + 0.092 * bell) * k,
        sq: 1.2,
      });
    }
    B.add(loft(edge, { seg: 5, capBottom: true, capTop: true, vFromY: true }), 'steel', W);

    /* The beak: a back-spike opposite the bit. Authored around the origin and
     * placed afterwards — place() rotates about the WORLD origin, which is how
     * the cambuk's hood plates ended up 0.70 m off the weapon. */
    const beak = blade([
      [-0.07 * k, 0.010 * k, 0.016 * k, 0],
      [0.00 * k, 0.013 * k, 0.020 * k, 0],
      [0.09 * k, 0.004 * k, 0.006 * k, 0],
    ], 5);
    B.add(place(place(beak, { rot: [0.95, 0, 0] }), { pos: [0, 0.34 * k, -0.075 * k] }), 'iron', DARK);

    // butt spike, so the haft's bottom end is not a blunt cut
    B.add(blade([
      [-0.20 * k, 0.014 * k, 0.014 * k, 0],
      [-0.30 * k, 0.005 * k, 0.005 * k, 0],
    ], 5), 'iron', DARK);
    B.add(tassel(-0.16 * k, 0.16 * k, 0.045 * k, 1.3), 'silk', W);
  },

  bow(B, k = 1) {
    // recurve limbs built as a single loft that curves in Z
    const N = 11, rings = [];
    for (let i = 0; i < N; i++) {
      const t = i / (N - 1);
      const y = (-0.62 + 1.24 * t) * k;
      const c = Math.sin(t * PI);
      const curve = (0.16 * Math.pow(Math.sin(t * PI), 0.7) - 0.10 * Math.sin(t * PI * 2) * (t < 0.5 ? 1 : -1)) * k;
      rings.push({ y, rx: (0.010 + 0.006 * c) * k, rz: (0.016 + 0.008 * c) * k, cz: -curve, sq: 1.8 });
    }
    B.add(loft(rings, { seg: 6, capTop: true, capBottom: true, vFromY: true }), 'wood', WARM, { sv: 3 });
    B.add(shaft(-0.09 * k, 0.09 * k, 0.021 * k, 0.021 * k, 7), 'leather', DARK);
    // string
    B.add(loft([
      { y: -0.62 * k, rx: 0.0035 * k, rz: 0.0035 * k, sq: 2, cz: -0.052 * k },
      { y: 0, rx: 0.0035 * k, rz: 0.0035 * k, sq: 2, cz: -0.052 * k },
      { y: 0.62 * k, rx: 0.0035 * k, rz: 0.0035 * k, sq: 2, cz: -0.052 * k },
    ], { seg: 4, vFromY: true }), 'neutral', [0.55, 0.5, 0.42]);
  },

  /** Faction banner on a pole — carried by standard bearers. */
  banner(B, k = 1) {
    B.add(shaft(-0.75 * k, 1.70 * k, 0.019 * k, 0.015 * k, 7), 'wood', WARM, { sv: 5 });
    B.add(ring(1.66 * k, 0.026 * k, 0.05 * k), 'gold', W);
    B.add(loft([
      { y: 1.72 * k, rx: 0.016 * k, rz: 0.016 * k, sq: 2 },
      { y: 1.80 * k, rx: 0.026 * k, rz: 0.026 * k, sq: 2 },
      { y: 1.90 * k, rx: 0.008 * k, rz: 0.008 * k, sq: 2 },
    ], { seg: 7, capTop: true, vFromY: true }), 'gold', W);
    // hanging cloth with a wave
    B.add(sheet((u, v, o) => {
      const y = (1.62 - 1.05 * v) * k;
      const x = (0.03 + u * 0.40) * k;
      const wav = Math.sin(u * 3.4 + v * 1.6) * 0.035 * k * (0.3 + u);
      o.set(x, y + wav * 0.4, wav);
    }, 4, 5, { thickness: 0.006 * k }), 'cape', W);
    B.add(tassel(1.60 * k, 0.22 * k, 0.05 * k, 0.8), 'silk', W);
  },
};

/**
 * Socket transforms, in HAND-BONE local space (see rig.js: bind hand axes are
 * world axes, arm hanging down). Tuned so the weapon sits in the fist with a
 * natural wrist angle once the rest pose is applied.
 */
export const SOCKETS = {
  greatGlaive: { bone: 'handR', pos: [-0.05, -0.05, 0.07], rot: [0.30, -1.15, -0.52], scale: 0.95 },
  spear: { bone: 'handR', pos: [-0.04, -0.05, 0.07], rot: [0.30, -0.9, -0.50], scale: 0.95 },
  dao: { bone: 'handR', pos: [-0.01, -0.05, 0.06], rot: [0.10, -1.25, -0.38] },
  halberd: { bone: 'handR', pos: [-0.04, -0.05, 0.07], rot: [0.30, -1.05, -0.50] },
  axe: { bone: 'handR', pos: [-0.01, -0.05, 0.06], rot: [0.14, -1.25, -0.38] },
  /* Two-handed, held like the glaive — the right hand is the pivot and the
   * left rides the haft, which is what the gada clips are authored against. */
  gada: { bone: 'handR', pos: [-0.05, -0.05, 0.07], rot: [0.30, -1.12, -0.52], scale: 1.0 },
  /* One hand, held low: a chain is swung from the wrist, not braced in two
   * hands like a polearm, so the grip sits closer to the palm and rolls less. */
  cambuk: { bone: 'handR', pos: [-0.02, -0.05, 0.06], rot: [0.16, -1.20, -0.40], scale: 1.0 },
  axeL: { bone: 'handL', pos: [0.01, -0.05, 0.06], rot: [0.30, 1.25, 0.42] },
  /* Paired, like the sabres: the same geometry in both fists, the left one
   * mirrored by a sign flip on X and on the Y/Z rotations rather than a
   * negative scale, which would invert the winding. Held a touch more upright
   * than a sabre because the mass is at the head, not along a blade. */
  kapak: { bone: 'handR', pos: [-0.01, -0.05, 0.06], rot: [0.18, -1.22, -0.34] },
  kapakL: { bone: 'handL', pos: [0.01, -0.05, 0.06], rot: [0.18, 1.22, 0.34] },
  // Off-hand sabre. Mirrors `dao` across the body's sagittal plane: the hand
  // bind axes are world axes (see the note above), so the mirror is a sign
  // flip on X and on the Y/Z rotations, not a scale of -1 — a negative scale
  // would invert the winding and turn the blade inside out under the shared
  // material's backface culling.
  daoL: { bone: 'handL', pos: [0.01, -0.05, 0.06], rot: [0.10, 1.25, 0.38] },
  bow: { bone: 'handL', pos: [0.01, -0.05, 0.06], rot: [0.38, 1.55, 0.42] },
  banner: { bone: 'handR', pos: [-0.04, -0.05, 0.07], rot: [0.28, -0.6, -0.48], scale: 1.0 },
};

export const WEAPON_NAMES = Object.keys(MODELS);

/**
 * Build every weapon geometry once.
 * @returns {Record<string, THREE.BufferGeometry>}
 */
export function buildWeaponGeometries(uvRects, scale = 1) {
  const out = {};
  for (const name in MODELS) {
    const B = new WeaponBuilder(uvRects);
    MODELS[name](B, scale);
    const g = B.build();
    g.userData.triangles = triCount(g);
    out[name] = g;
  }
  return out;
}

/** Create a renderable weapon mesh. */
export function makeWeapon(name, geometries, material) {
  const g = geometries[name];
  if (!g) return null;
  const m = new THREE.Mesh(g, material);
  m.name = 'weapon_' + name;
  m.castShadow = true;
  m.receiveShadow = false;
  m.frustumCulled = false;
  return m;
}

/**
 * Attach a weapon (or any Object3D) to a bone of a SkinnedMesh.
 * @param {THREE.SkinnedMesh} mesh
 * @param {string} boneName
 * @param {THREE.Object3D} obj
 * @param {{pos?:number[], rot?:number[], scale?:number}} socket
 */
export function attach(mesh, boneName, obj, socket) {
  const bone = mesh?.skeleton?.getBoneByName?.(boneName) || mesh?.userData?.bones?.[boneName];
  if (!bone || !obj) return null;
  const s = socket || { pos: [0, -0.06, 0.03], rot: [0, 0, 0] };
  obj.position.fromArray(s.pos || [0, 0, 0]);
  obj.rotation.set((s.rot && s.rot[0]) || 0, (s.rot && s.rot[1]) || 0, (s.rot && s.rot[2]) || 0, 'YXZ');
  if (s.scale) obj.scale.setScalar(s.scale);
  bone.add(obj);
  return obj;
}
